import React, { useState, useRef } from "react";
import {
  Box,
  Popper,
  Button,
  ClickAwayListener,
  Grow,
  Paper,
  MenuList,
  MenuItem,
  Divider,
} from "@material-ui/core";
import { ChevronLeft } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useStyles } from "./children.styles";
import Typography from "../../../components/Typography";
import useMe from "../../../utils/use-me";
import { useAuth } from "../../../utils/use-auth";
import CompanyMenuItem from "./CompanyMenuItem";
import ProfileIcon from "../../../assets/icons/basic/ProfileIcon";

const TopHeaderMenu = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const { logout, isLoggedIn } = useAuth();

  const { me, switchUser, selectedCompany, isCompanyRoot, isRoot } = useMe();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  if (!isLoggedIn || !me) return null;

  const handleToggle = () => {
    setOpen(prev => !prev);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        ref={anchorRef}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Box display="flex" alignItems="center" justifyContent="center">
          <Box
            style={{
              marginRight: "3px",
              marginBottom: "1px",
              height: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ProfileIcon width={14} height={14} color="#808080" />
          </Box>
          <Typography customVariant="subtitle3" color="textSecondary">
            {(selectedCompany ?? me)?.name}
          </Typography>

          <ChevronLeft
            className={`${classes.chevron} ${open && classes.chevronActive}`}
          />
        </Box>
      </Button>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        style={{ zIndex: 1 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {me.companies.length > 0 &&
                    me.companies.map(company => {
                      if (company.id !== selectedCompany.id) return null;

                      return (
                        <CompanyMenuItem
                          key={company.id}
                          company={company}
                          switchUser={switchUser}
                          handleClose={handleClose}
                          disabled
                        />
                      );
                    })}

                  {me.companies.length > 1 &&
                    me.companies.map(company => {
                      if (company.id === selectedCompany.id) return null;

                      return (
                        <CompanyMenuItem
                          key={company.id}
                          company={company}
                          switchUser={switchUser}
                          handleClose={handleClose}
                          disabled={false}
                        />
                      );
                    })}

                  {me.companies.length > 1 && <Divider />}

                  <MenuItem
                    onClick={e => {
                      history.push("/account");
                      handleClose(e);
                    }}
                    classes={{ root: classes.langItem }}
                  >
                    <Typography
                      variant="body2"
                      classes={{ root: classes.language }}
                    >
                      {t("pages.account")}
                    </Typography>
                  </MenuItem>

                  {isCompanyRoot && (
                    <MenuItem
                      onClick={e => {
                        history.push("/users");
                        handleClose(e);
                      }}
                      classes={{ root: classes.langItem }}
                    >
                      <Typography
                        variant="body2"
                        classes={{ root: classes.language }}
                      >
                        {t("pages.users")}
                      </Typography>
                    </MenuItem>
                  )}

                  {isRoot && (
                    <MenuItem
                      onClick={e => {
                        history.push("/logs");
                        handleClose(e);
                      }}
                      classes={{ root: classes.langItem }}
                    >
                      <Typography
                        variant="body2"
                        classes={{ root: classes.language }}
                      >
                        {t("pages.logs")}
                      </Typography>
                    </MenuItem>
                  )}

                  <MenuItem
                    onClick={logout}
                    classes={{ root: classes.langItem }}
                  >
                    <Typography
                      variant="body2"
                      classes={{ root: classes.language }}
                    >
                      {t("buttons.logout")}
                    </Typography>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default TopHeaderMenu;
